import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import Breadcrumb from '../components/Breadcrumb'
import Button from 'react-bootstrap/Button'
import CardLists from '../components/CardLists'
import DecksTable from '../components/DecksTable'
import Description from '../components/Description'
import Error from '../components/Error'
import Form from 'react-bootstrap/Form'
import { Helmet } from 'react-helmet'
import Mana from '../components/Mana'
import { OutboundLink } from 'react-ga'
import Panels from '../components/Panels'
import RelatedInfo from '../components/RelatedInfo'
import SubNav from '../components/SubNav'
import classNames from 'classnames'
import { fetchPageData } from '../store/pagedata'
import { formatUrl } from '../util/helpers'
import reactStringReplace from 'react-string-replace'
import styles from '../styles/default.module.scss'
import useDarkMode from 'use-dark-mode'
import { useLocation } from 'react-router-dom'
import { useParams } from 'react-router-dom'

const Default = () => {
  const { dir, page, subpage } = useParams()
  const darkMode = useDarkMode(false)
  const dispatch = useDispatch()
  const myRef = useRef(null)
  const cards = useSelector((state) => state.cards)
  const data = useSelector((state) => state.pagedata.data)
  const error = useSelector((state) => state.pagedata.error)
  const isLoading = useSelector((state) => state.pagedata.isLoading)
  const scrollKey = useSelector((state) => state.pagedata.scrollKey)
  const { pathname, search } = useLocation()
  const [prevScrollKey, setPrevScrollKey] = useState(false)
  const [shouldScroll, setShouldScroll] = useState(false)

  // If scroll key is updated, prepare to scroll window when done loading
  useEffect(() => {
    if (myRef.current && scrollKey !== prevScrollKey) {
      setShouldScroll(true)
      setPrevScrollKey(scrollKey)
    }
  }, [scrollKey])

  // Scroll when done loading
  useEffect(() => {
    if (!isLoading && shouldScroll) {
      myRef.current.scrollIntoView()
      setShouldScroll(false)
    }
  }, [isLoading, shouldScroll])

  useEffect(async () => {
    dispatch(fetchPageData())
  }, [pathname, search])

  if (error) {
    return (
      <>
        <Helmet>
          <title>{error ? 'Error' : 'Loading...'} | EDHREC</title>
        </Helmet>
        <Error error={error} />
      </>
    )
  }

  const alternateTitles = {
    sets: 'All Sets',
    themes: 'All Themes',
    tribes: 'All Tribes',
  }
  const title = data?.container?.title || alternateTitles[dir]

  const comboCardNames = data?.container?.comboHeader?.cardviews?.map(
    ({ name }) => name
  )
  const comboArchidekt = comboCardNames
    ? `https://archidekt.com/search/decks?cards=${encodeURI(
        comboCardNames.map((name) => `"${name}"`).join(',')
      )}&formats=3&orderBy=-updatedAt`
    : ''
  const comboDeckstats = comboCardNames
    ? `https://deckstats.net/decks/search/?search_format=10&${encodeURI(
        comboCardNames
          ?.map((card) => cards[formatUrl(card)])
          ?.map(
            (card) =>
              `search_cards${card?.legal_commander ? '_commander' : ''}[]=${
                card?.name
              }`
          )
          .join('&')
          .replace(' ', '+')
      )}`
    : ''

  return (
    <>
      <Helmet>
        <meta
          name='description'
          content='EDH Recommendations and strategy content for Magic: the Gathering Commander'
        />
        {data?.container && (
          <meta
            name='keywords'
            content={`${data.container.keywords}${
              data.container.keywords && ','
            }EDHREC,EDH,recommendations,Commander,MTG,Magic,staples,strategy,content`}
          />
        )}
        <title>{title ? `${title} | EDHREC` : 'EDHREC'}</title>
      </Helmet>
      <div
        className={classNames(styles.container, {
          [styles.dark]: darkMode.value,
        })}
      >
        <Breadcrumb
          breadcrumb={data?.breadcrumb || data?.container?.breadcrumb}
        />
        <>
          <div className={styles.headingContainer}>
            <h1 className='page-heading'>{title}</h1>
          </div>
          {dir === 'combos' && !page ? (
            <>
              <Description>{data?.description}</Description>
              <div className={styles.relatedInfoContainer}>
                {data?.related_info?.map(({ header, items }) => (
                  <SubNav
                    items={items.map(({ colors, count, textLeft, url }) => {
                      return {
                        image: <Mana colors={colors} />,
                        padLeft: true,
                        textLeft,
                        textRight: `${count} combos`,
                        url,
                      }
                    })}
                    key={header}
                  />
                ))}
              </div>
            </>
          ) : dir === 'decks' ? (
            <>
              <Panels />
              <DecksTable rows={data?.table} />
            </>
          ) : dir === 'sets' && !page && data?.data ? (
            <>
              <Description>
                These are the most popular cards from each set. Click a card
                below for more cards from that set.
              </Description>
              <SubNav
                items={data.data.map(({ code, icon_svg_uri, name }) => ({
                  image: (
                    <img
                      alt={code}
                      className={styles.setIcon}
                      src={icon_svg_uri}
                    />
                  ),
                  textLeft: name,
                  url: `/sets/${code}/`,
                }))}
              />
            </>
          ) : ['themes_DISABLED', 'tribes_DISABLED'].includes(dir) && !page ? (
            <>
              {dir === 'tribes' ? (
                <Description>
                  Decks are considered tribal if they have more than 10
                  creatures and at least 60.0% of them share a tribe.
                </Description>
              ) : dir === 'themes' ? (
                <Description>
                  These are various deck building themes and archetypes. Click a
                  card below for more info on that theme.
                </Description>
              ) : null}
              <SubNav
                items={
                  data?.container.json_dict?.cardlists &&
                  data.container.json_dict.cardlists[0].cardviews.map(
                    ({ image_uris, label, url, name }) => {
                      const count = parseInt(label).toString()
                      const page = label.slice(
                        [count.length + 1],
                        label.length - 6
                      )
                      return {
                        image: (
                          <img
                            alt={name}
                            className={styles.art}
                            src={`https://c1.scryfall.com/file/scryfall-cards/normal/front/${image_uris[0]}`}
                          />
                        ),
                        textLeft: page,
                        textRight: `${count} decks`,
                        url: url,
                      }
                    }
                  )
                }
              />
            </>
          ) : /^\/(commanders|top)\/?(month|salt|salt-2019|week|year)?\/?$/.test(
              pathname
            ) ? (
            <>
              {data?.description && (
                <Description>{data.description}</Description>
              )}
              {!pathname.includes('salt') && (
                <SubNav
                  items={[
                    {
                      textLeft: 'Past Week',
                      url: `/${dir}/week`,
                    },
                    {
                      textLeft: 'Past Month',
                      url: `/${dir}/month`,
                    },
                    {
                      textLeft: 'Past 2 Years',
                      url: `/${dir}/year`,
                    },
                  ]}
                />
              )}
              {data?.container && <CardLists />}
            </>
          ) : (
            <>
              <RelatedInfo {...data?.relatedinfo} />
              {(([
                'average-decks',
                'cards',
                'commanders',
                'deckpreview',
                'decks',
              ].includes(dir) &&
                ![
                  'w',
                  'u',
                  'b',
                  'r',
                  'g',
                  'wu',
                  'ub',
                  'br',
                  'rg',
                  'gw',
                  'wb',
                  'ur',
                  'bg',
                  'rw',
                  'gu',
                  'wub',
                  'ubr',
                  'brg',
                  'rgw',
                  'gwu',
                  'wbg',
                  'urw',
                  'bgu',
                  'rwb',
                  'gur',
                  'wubr',
                  'ubrg',
                  'brgw',
                  'rgwu',
                  'gwub',
                  'wubrg',
                  'colorless',
                ].includes(page)) ||
                (dir === 'combos' && subpage)) && <Panels />}
              {(!isLoading || ['average-decks'].includes(dir)) && (
                <Description
                  cards={data?.description2cards}
                  posts={data?.description2posts}
                  wordpress={!!data?.description2}
                >
                  {data?.combo_id ? (
                    <>
                      <Form.Group>
                        {reactStringReplace(
                          data?.description,
                          /\{([^}]+)\}/,
                          (match) => (
                            <Mana color={match} />
                          )
                        )}
                      </Form.Group>
                      <hr className='mb-3' />
                      <Form.Label>View on:</Form.Label>
                      <Form.Group>
                        <OutboundLink
                          alt='View combo'
                          className='mr-2'
                          eventLabel={`https://commanderspellbook.com/combo/${data.combo_id}/`}
                          target='_blank'
                          to={`https://commanderspellbook.com/combo/${data.combo_id}/`}
                        >
                          <Button>Commander Spellbook</Button>
                        </OutboundLink>
                        <OutboundLink
                          alt='View on Archidekt'
                          className='mr-2'
                          eventLabel={comboArchidekt}
                          target='_blank'
                          to={comboArchidekt}
                        >
                          <Button>Archidekt</Button>
                        </OutboundLink>
                        <OutboundLink
                          alt='View on Deckstats'
                          eventLabel={comboDeckstats}
                          target='_blank'
                          to={comboDeckstats}
                        >
                          <Button>Deckstats</Button>
                        </OutboundLink>
                      </Form.Group>
                      <Form.Label>Report an error with this combo:</Form.Label>
                      <Form.Group className='mb-0'>
                        <OutboundLink
                          alt='Report combo'
                          eventLabel={`https://commanderspellbook.com/report-error?comboId=${data.combo_id}`}
                          target='_blank'
                          to={`https://commanderspellbook.com/report-error?comboId=${data.combo_id}`}
                        >
                          <Button variant='danger'>Report error</Button>
                        </OutboundLink>
                      </Form.Group>
                    </>
                  ) : (
                    data?.description2 || data?.description
                  )}
                </Description>
              )}
              {data?.container && dir !== 'average-decks' && <CardLists />}
            </>
          )}
        </>
      </div>
    </>
  )
}

export default Default
